/**
 * Abzugsteuer bei Creatorn im Ausland (Hinweis, keine Rechtsberatung). Zahlt eine Agentur mit Sitz in Deutschland einem
 * im Ausland ansässigen Creator eine Vergütung für die Überlassung von Rechten (Nutzungsrechte, Whitelisting, Spark Ads,
 * Buyout), muss sie nach § 50a EStG Steuer einbehalten und abführen, außer eine Freistellungsbescheinigung des BZSt liegt
 * zum Zahlungstag vor; sie haftet sonst selbst. Österreich: § 99 EStG (20 %). Die reine Erstellung und Veröffentlichung
 * von Inhalten ist in Deutschland nach herrschender Auffassung nicht abzugspflichtig – bei gemischter Vergütung ist
 * aufzuteilen. Rein; liefert Warnung, Bemessungsgrundlage und Satz für die Auszahlung.
 */
import { csvFormulaGuard } from "./csv.js";
import type { Cents, IsoDate } from "./types.js";

export interface WithholdingLine {
  description: string;
  netCents: Cents;
  /** Positionsart aus dem Katalog (z. B. usage_rights, whitelisting, spark_ads, buyout) */
  kind?: string | null;
}

export interface WithholdingInput {
  agencyCountry: string | null;
  /** Land, in dem der Creator steuerlich ansässig ist (Wohnsitz) */
  creatorCountry: string | null;
  lines: readonly WithholdingLine[];
  paymentDate: IsoDate;
  /** gültig bis (Freistellungsbescheinigung des BZSt bzw. Entlastung) */
  exemptionValidUntil?: IsoDate | null;
  /** Sprache des Hinweises (Standard Deutsch) */
  language?: "de" | "en";
}

export interface WithholdingResult {
  applies: boolean;
  law: "§ 50a EStG" | "§ 99 EStG" | null;
  /** Satz in Prozent auf die Bemessungsgrundlage (DE: 15 % zzgl. 5,5 % Solidaritätszuschlag = 15,825 %) */
  ratePercent: number | null;
  basisCents: Cents;
  amountCents: Cents;
  /** Positionen, die als Rechteüberlassung erkannt wurden */
  rightsLines: string[];
  message: string | null;
}

const RIGHTS_KINDS = new Set(["usage_rights", "whitelisting", "spark_ads", "buyout", "license", "licence", "rights"]);
const RIGHTS_TEXT = /(nutzungsrecht|lizenz|license|licen[cs]e|whitelist|spark ?ads?|buy-?out|verwertungsrecht|werbe(nutzung|rechte)|paid usage|usage rights)/i;

const RULES: Record<string, { law: WithholdingResult["law"]; ratePercent: number }> = {
  DE: { law: "§ 50a EStG", ratePercent: 15.825 },
  AT: { law: "§ 99 EStG", ratePercent: 20 },
};

export function withholdingCheck(input: WithholdingInput): WithholdingResult {
  const agency = (input.agencyCountry ?? "").toUpperCase();
  const creator = (input.creatorCountry ?? "").toUpperCase();
  const rule = RULES[agency];
  const none: WithholdingResult = { applies: false, law: null, ratePercent: null, basisCents: 0, amountCents: 0, rightsLines: [], message: null };
  if (!rule || !creator || creator === agency) return none;
  const rights = input.lines.filter((l) => (l.kind && RIGHTS_KINDS.has(l.kind)) || RIGHTS_TEXT.test(l.description));
  if (!rights.length) return none;
  const basisCents = rights.reduce((a, l) => a + l.netCents, 0);
  if (input.exemptionValidUntil && input.exemptionValidUntil >= input.paymentDate) {
    return { ...none, law: rule.law, basisCents, rightsLines: rights.map((l) => l.description),
      message: input.language === "en"
        ? `Exemption certificate on file (valid until ${input.exemptionValidUntil}); no withholding under ${rule.law}.`
        : `Freistellungsbescheinigung liegt vor (gültig bis ${input.exemptionValidUntil}); kein Einbehalt nach ${rule.law}.` };
  }
  const amountCents = Math.round((basisCents * rule.ratePercent) / 100);
  return {
    applies: true, law: rule.law, ratePercent: rule.ratePercent, basisCents, amountCents, rightsLines: rights.map((l) => l.description),
    message: input.language === "en"
      ? `Creator abroad (${creator}) receives remuneration for usage rights. Under ${rule.law}, without an exemption certificate, tax must be `
        + `withheld and remitted (${String(rule.ratePercent)} % on the rights items); otherwise the agency is liable itself. `
        + "Please clarify with your tax adviser before the payout."
      : `Creator im Ausland (${creator}) erhält eine Vergütung für Nutzungsrechte. Nach ${rule.law} ist ohne Freistellungsbescheinigung `
        + `Steuer einzubehalten und abzuführen (${String(rule.ratePercent).replace(".", ",")} % auf die Rechte-Positionen); die Agentur haftet sonst selbst. `
        + "Bitte vor der Auszahlung mit der Steuerberatung klären.",
  };
}

/**
 * Anmeldezeitraum des Einbehalts (GELD-13): Deutschland meldet vierteljährlich (§ 50a Abs. 5 EStG, bis zum 10. nach dem
 * Quartal) als „2026-Q3“, Österreich monatlich (§ 100 EStG, bis zum 15. des Folgemonats) als „2026-09“.
 */
export function withholdingPeriod(law: WithholdingResult["law"], paymentDate: IsoDate): string {
  const y = paymentDate.slice(0, 4);
  const m = Number(paymentDate.slice(5, 7));
  return law === "§ 99 EStG" ? `${y}-${String(m).padStart(2, "0")}` : `${y}-Q${Math.ceil(m / 3)}`;
}

export interface WithholdingExportRow {
  period: string;
  law: string;
  creator: string;
  country: string | null;
  invoiceNumber: string | null;
  paidOn: IsoDate;
  ratePercent: number;
  withheldCents: Cents;
  paidOutCents: Cents;
  currency: string;
}

/** Eigener Export der einbehaltenen Abzugsteuer je Anmeldezeitraum (CSV, Semikolon, UTF-8 mit BOM, Dezimalkomma). */
export function buildWithholdingCsv(rows: readonly WithholdingExportRow[]): string {
  const cell = (v: string) => (/[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const num = (c: number) => (c / 100).toFixed(2).replace(".", ",");
  const head = ["Anmeldezeitraum", "Rechtsgrundlage", "Creator", "Land", "Rechnung", "Auszahlung am", "Satz %", "Einbehalt", "Ausgezahlt", "Währung"];
  const sorted = [...rows].sort((a, b) => a.period.localeCompare(b.period) || a.paidOn.localeCompare(b.paidOn) || a.creator.localeCompare(b.creator, "de"));
  // Textzellen (Creator-Name, Rechnungsnummer …) gegen Formeln schützen; Satz und Beträge bleiben Zahlen
  const text = (v: string) => cell(csvFormulaGuard(v));
  const lines = sorted.map((r) => [text(r.period), text(r.law), text(r.creator), text(r.country ?? ""), text(r.invoiceNumber ?? ""), text(r.paidOn),
    cell(String(r.ratePercent).replace(".", ",")), cell(num(r.withheldCents)), cell(num(r.paidOutCents)), text(r.currency)].join(";"));
  return `﻿${[head.join(";"), ...lines].join("\r\n")}\r\n`;
}
